import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { TALK, WAKE_CHIPS } from "@/lib/sleepData";

export default function NightTalk({ onDone, onLeave }) {
  const [step, setStep] = useState(0);
  const [wake, setWake] = useState("07:00");
  const [pattern, setPattern] = useState(null);
  const t = TALK[step];

  const advance = (payload) => {
    const next = { wake, pattern, ...payload };
    setWake(next.wake);
    setPattern(next.pattern);
    if (step + 1 >= TALK.length) onDone(next);
    else setStep(step + 1);
  };

  return (
    <div className="w-full max-w-2xl" data-testid="sleep-talk">
      <div className="flex gap-1.5 mb-9 max-w-[200px]">
        {TALK.map((_, i) => (
          <span key={i} className="h-[3px] flex-1 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.14)" }}>
            <motion.span className="block h-full" initial={false} animate={{ width: i <= step ? "100%" : "0%" }} transition={{ duration: 0.4 }} style={{ background: "#EBD9A6" }} />
          </span>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={t.id} initial={{ opacity: 0, y: 26, filter: "blur(7px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }} exit={{ opacity: 0, y: -20, filter: "blur(7px)" }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}>
          <h2 className="font-display text-[32px] sm:text-[46px] leading-[1.04] text-white">{t.prompt}</h2>
          <p className="text-[15px] mt-3.5 max-w-lg text-white/50">{t.sub}</p>

          {t.kind === "time" ? (
            <div className="mt-9">
              <div className="flex items-end gap-5 flex-wrap">
                <input type="time" value={wake} onChange={(e) => setWake(e.target.value || "07:00")}
                  className="bg-transparent border-b outline-none font-display text-[46px] sm:text-[64px] leading-none pb-2 tabular-nums text-white"
                  style={{ borderColor: "rgba(235,217,166,0.4)", colorScheme: "dark" }} data-testid="talk-wake-input" />
                <button onClick={() => advance({ wake })} className="rounded-full px-7 py-4 text-sm font-semibold inline-flex items-center gap-2.5"
                  style={{ background: "#EBD9A6", color: "#171B3C", boxShadow: "0 22px 46px -22px rgba(235,217,166,0.7)" }} data-testid="talk-wake-next">
                  Build it backwards <ArrowRight size={15} />
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mt-7">
                {WAKE_CHIPS.map((c, i) => (
                  <motion.button key={c} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.05 }}
                    onClick={() => setWake(c)} className="rounded-full px-4 py-2 text-[13px] tabular-nums transition-colors"
                    style={wake === c ? { background: "rgba(235,217,166,0.2)", color: "#EBD9A6", border: "1px solid rgba(235,217,166,0.5)" } : { background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.6)", border: "1px solid rgba(255,255,255,0.08)" }}
                    data-testid={`talk-wake-chip-${c}`}>{c}</motion.button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-8 space-y-2.5">
              {t.options.map((o, i) => (
                <motion.button key={o.label} initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.08 + i * 0.06 }}
                  onClick={() => advance(t.id === "pattern" ? { pattern: o.key } : { need: o.hours })}
                  className="group w-full text-left rounded-2xl px-5 py-4 flex items-center gap-4 transition-all hover:-translate-y-0.5"
                  style={{ background: "rgba(255,255,255,0.055)", border: "1px solid rgba(255,255,255,0.09)", backdropFilter: "blur(8px)" }}
                  data-testid={`talk-option-${step}-${i}`}>
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "rgba(235,217,166,0.6)" }} />
                  <span className="font-display text-[17px] sm:text-lg leading-snug text-white/90">{o.label}</span>
                  <ArrowRight size={14} className="ml-auto shrink-0 opacity-0 group-hover:opacity-70 transition-opacity" style={{ color: "#EBD9A6" }} />
                </motion.button>
              ))}
            </div>
          )}

          <button onClick={() => (step > 0 ? setStep(step - 1) : onLeave())} className="mt-9 text-xs inline-flex items-center gap-1.5 text-white/35" data-testid="talk-back">
            <ArrowLeft size={11} /> {step > 0 ? "back" : "not now"}
          </button>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
